import React, { useState, useEffect, useRef } from 'react';
import Section from './Section';
import { X_PADDING } from '../constants';
import Page from './Page';
import {
  FaUsersCog,
  FaBuilding,
  FaBullhorn,
  FaUsers,
  FaFileSignature,
  FaGraduationCap,
  FaBalanceScale,
  FaHandshake,
  FaFlask,
} from 'react-icons/fa';

const About = () => {
  return (
    <Page name="About COAZ">
      <div className="w-full pt-8 bg-white">
        {/* Intro Section */}
        <Section
          name="About COAZ"
          description="Founded in 1967, the Clinical Officers Association of Zambia (COAZ) is the largest and
          only national association that convenes 110+ District Branches and Sub-division Clinical Professional 
          societies and other critical stakeholders."
        />

        {/* Detailed Description with Image and Vision/Mission */}
        <div className={`${X_PADDING} w-full mx-auto py-12`}>
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Left Column - Image */}
            <div className="w-full lg:w-2/5">
              <div 
                className="w-full h-[400px] rounded-lg shadow-lg bg-cover bg-center"
                style={{ backgroundImage: 'url(/images/img_15.jpg)' }}
              >
                <div className="w-full h-full bg-gradient-to-t from-[#1477c2]/30 to-transparent rounded-lg"></div>
              </div>
            </div>

            {/* Right Column - Vision, Aim, Mission */}
            <div className="w-full lg:w-3/5">
              <div className="space-y-6">
                {/* Vision */}
                <div className="border-l-4 border-[#1477c2] pl-6">
                  <h3 className="text-[#1477c2] font-nunitoSansBold text-sm tracking-wider uppercase mb-1">Vision</h3>
                  <p className="text-[#16365c] font-nunitoSansRegular text-base leading-relaxed">
                    To lead in enhancing the role and effectiveness of clinical officers in delivering quality healthcare services across Zambia
                  </p>
                </div>

                {/* Aim */}
                <div className="border-l-4 border-[#1b9de3] pl-6">
                  <h3 className="text-[#1b9de3] font-nunitoSansBold text-sm tracking-wider uppercase mb-1">Aim</h3>
                  <p className="text-[#16365c] font-nunitoSansRegular text-base leading-relaxed">
                    The primary aim is to enhance the role and impact of clinical officers in Zambia's healthcare system
                  </p>
                </div>

                {/* Mission */}
                <div className="border-l-4 border-[#16365c] pl-6">
                  <h3 className="text-[#16365c] font-nunitoSansBold text-sm tracking-wider uppercase mb-1">Mission</h3>
                  <p className="text-[#16365c] font-nunitoSansRegular text-base leading-relaxed">
                    To advocate for and empower clinical officers to achieve excellence
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Description Paragraph */}
        <div className={`${X_PADDING} w-full mx-auto pb-12`}>
          <div className="bg-[#f8fafb] rounded-xl p-8 md:p-10 border border-[#eef1f4]">
            <p className="text-[#16365c] font-nunitoSansRegular text-base leading-relaxed mb-6">
              As the Clinicians' powerful ally in Healthcare, the COAZ delivers on this mission by representing Clinical officers and clinical Practitioners with a unified voice in health and legislative bodies across the nation, removing obstacles that interfere with our practice, patient care, leading the charge to decentralized health care and confront public health crises, and driving the future to tackle the biggest challenges in health care and training the leaders of tomorrow.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg p-5 border border-[#eef1f4]">
                <h4 className="text-[#1477c2] font-nunitoSansBold text-sm tracking-wider uppercase mb-2">Governance Structure</h4>
                <p className="text-[#16365c] font-nunitoSansRegular text-sm leading-relaxed">
                  The COAZ system of governance and policy making include the National Executive Committee, Committees of the Executive, National Investment Board. In Administration are Employees and staff of the association.
                </p>
              </div>
              <div className="bg-white rounded-lg p-5 border border-[#eef1f4]">
                <h4 className="text-[#1477c2] font-nunitoSansBold text-sm tracking-wider uppercase mb-2">Registration</h4>
                <p className="text-[#16365c] font-nunitoSansRegular text-sm leading-relaxed">
                  The Association was formally re-registered under Societies Act Cap 118 in 2012 to promote Clinical practitioners' welfare, provide continuous professional development, promote community involvement and support Healthcare service delivery in Zambia as a means of championing interests of our members.
                </p>
              </div>
            </div>

            <p className="text-[#16365c] font-nunitoSansRegular text-sm leading-relaxed mt-4 italic border-t border-[#eef1f4] pt-4">
              This is achieved through: Training, Capacity building, Advocacy, Mobilization, Socio-economic empowerment, Policy engagements   
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div className={`${X_PADDING} w-full mx-auto`}>
          <Statistics />
        </div>

        {/* Governance Section */}
        <div className={`${X_PADDING} w-full mx-auto py-16`}>
          <Section
            name="Governance"
            description="The COAZ system of governance and policy making include the National Executive Committee, Committees of the Executive, National Investment Board."
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <EquityFeatureCard 
                title="National Executive Committee"
                description="The supreme governing body responsible for strategic direction and policy oversight of the association."
              />
              <EquityFeatureCard 
                title="Committees of the Executive"
                description="Specialized committees that handle specific portfolios including finance, education, and advocacy."
              />
              <EquityFeatureCard 
                title="National Investment Board"
                description="Oversees the association's investments and financial sustainability initiatives."
              />
            </div>
          </Section>
        </div>

        {/* Advocacy Section */}
        <div className={`${X_PADDING} w-full mx-auto py-16`}>
          <Section
            name="Advocacy"
            description="The professional interests of our members, regulations & policies, laws, clinical professions community of practice and health service delivery."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AdvocacyGridItem 
                icon={<FaBullhorn className="text-[#1477c2] text-2xl" />}
                title="Policy Engagement"
                text="Actively engaging with policymakers to shape healthcare policies that benefit clinical officers and patients."
              />
              <AdvocacyGridItem 
                icon={<FaBalanceScale className="text-[#1477c2] text-2xl" />}
                title="Legal Advocacy"
                text="Representing clinical officers in legal matters and advocating for fair treatment in the healthcare system."
              />
              <AdvocacyGridItem 
                icon={<FaUsers className="text-[#1477c2] text-2xl" />}
                title="Community of Practice"
                text="Building a strong community of clinical professionals to share knowledge and best practices."
              />
            </div>
          </Section>
        </div>

        {/* Key Priority Areas */}
        <div className={`${X_PADDING} w-full mx-auto py-16`}>
          <Section
            name="Key Priority Areas"
            description="Our focus areas to achieve excellence in healthcare delivery and professional development."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PriorityPoint 
                icon={<FaGraduationCap className="text-[#1b9de3]" />}
                text="Continuing Professional Development (CPD) programs for clinical officers"
              />
              <PriorityPoint 
                icon={<FaHandshake className="text-[#1b9de3]" />}
                text="Strengthening partnerships with healthcare stakeholders and government"
              />
              <PriorityPoint 
                icon={<FaBuilding className="text-[#1b9de3]" />}
                text="Infrastructure development and resource mobilization"
              />
              <PriorityPoint 
                icon={<FaFlask className="text-[#1b9de3]" />}
                text="Research and innovation in clinical practice"
              />
              <PriorityPoint 
                icon={<FaUsersCog className="text-[#1b9de3]" />}
                text="Welfare and socio-economic empowerment of members"
              />
              <PriorityPoint 
                icon={<FaFileSignature className="text-[#1b9de3]" />}
                text="Policy advocacy and legislative engagement"
              />
            </div>
          </Section>
        </div>
      </div>
    </Page>
  );
};

export default About;

const Statistics = () => {
  const statisticsRef = useRef(null);
  const [startCounter, setStartCounter] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStartCounter(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (statisticsRef.current) {
      observer.observe(statisticsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={statisticsRef} className='w-full h-fit pb-12'>
      <div className='w-full h-fit px-4 xs:px-[8%] sm:px-[13%] md:px-[5%] xl:px-[13%]'>
        <div className='flex w-full h-fit md:h-[157.2px] px-8 pt-8 pb-12 md:pt-0 md:py-0 z-10 items-center bg-white shadow-lg shadow-gray-200 xs:-translate-y-[52px] shrink-0'>
          <div className='flex flex-col md:flex-row space-y-6 md:space-y-0 md:justify-between w-full h-fit overflow-hidden'>
            <Statistic statistic='6000' text='REGISTERED MEMBERS' startCounter={startCounter} />
            <Statistic statistic='116' text='ACTIVE DISTRICTS' startCounter={startCounter} />
            <Statistic statistic='35' text='STAFF MEMBERS' startCounter={startCounter} />
            <Statistic statistic='10' text='OUR PARTNERS' startCounter={startCounter} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Statistic = ({ statistic, text, startCounter }) => {
  const numericValue = Number(String(statistic).replace(/\D/g, '')) || 0;

  return (
    <div className='w-fit h-fit overflow-hidden'>
      <div className='statistic flex flex-col'>
        <div className='relative flex items-center h-[72px] after:absolute after:bottom-[5px] after:left-0 after:w-[35px] after:h-0.5 after:bg-theme'>
          <p
            style={{ '--num': startCounter ? numericValue : 0 }}
            className='number font-nunitoSansSemiBold text-[40px]/[40px]'
          />
        </div>
        <p className='font-nunitoSansextraBold text-sm text-[rgba(0,0,0,.8)] tracking-[1px]'>
          {text}
        </p>
      </div>
    </div>
  );
};

// Equity-style Feature Card - No hover effects
const EquityFeatureCard = ({ title, description }) => {
  return (
    <div className="w-full h-48">
      <div className="feature flex flex-col space-y-2 items-center justify-center p-6 overflow-hidden rounded-lg bg-[#eef7fd] text-[#16365c]">
        <h4 className="text-base font-bold text-center text-[#16365c]">
          {title}
        </h4>
        <p className="text-xs text-center text-[#7c8a97]">
          {description}
        </p>
      </div>
    </div>
  );
};

// Advocacy Grid Item Component - Shadow instead of borders, no hover
const AdvocacyGridItem = ({ icon, title, text }) => {
  return (
    <div className="rounded-lg p-4 bg-white shadow-md">
      <div className="flex flex-col">
        <div className="mb-2">{icon}</div>
        <h4 className="text-sm font-bold mb-1 text-[#16365c]">
          {title}
        </h4>
        <p className="text-[#7c8a97] text-xs leading-relaxed">{text}</p>
      </div>
    </div>
  );
};

// Priority Point Component
const PriorityPoint = ({ icon, text }) => {
  return (
    <li className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#eef1f4]">
      <span className="text-[#1b9de3] mt-0.5 flex-shrink-0">{icon}</span>
      <span className="text-[#16365c] text-sm leading-relaxed">{text}</span>
    </li>
  );
};